/**
 * Format a timestamp as a relative time string
 * Examples: "Just now", "5 min ago", "Today at 2:30 PM", "Yesterday", "Mon, Jan 15"
 */
export function formatRelativeTime(timestamp: number | undefined | null): string {
  // Handle invalid timestamps
  if (!timestamp || isNaN(timestamp) || timestamp <= 0) {
    return 'Recently';
  }

  const now = Date.now();
  const diff = now - timestamp;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  const date = new Date(timestamp);

  // Check for invalid date
  if (isNaN(date.getTime())) {
    return 'Recently';
  }

  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  // Just now (< 1 min)
  if (minutes < 1) {
    return 'Just now';
  }

  // X min ago (< 1 hour)
  if (hours < 1) {
    return `${minutes} min ago`;
  }

  // Today at HH:MM
  if (isSameDay(date, today)) {
    return `Today at ${formatTime(date)}`;
  }

  // Yesterday
  if (isSameDay(date, yesterday)) {
    return 'Yesterday';
  }

  // This week: "Mon, Jan 15"
  if (days < 7) {
    return formatShortDate(date);
  }

  // This year: "Jan 15"
  if (date.getFullYear() === today.getFullYear()) {
    return formatMonthDay(date);
  }

  // Older: "Jan 15, 2024"
  return formatFullDate(date);
}

/**
 * Get a group label for a timestamp
 * Examples: "Today", "Yesterday", "This Week", "This Month", "Older"
 */
export function getDateGroup(timestamp: number | undefined | null): string {
  // Handle invalid timestamps
  if (!timestamp || isNaN(timestamp) || timestamp <= 0) {
    return 'Older';
  }

  const now = Date.now();
  const diff = now - timestamp;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  const date = new Date(timestamp);

  // Check for invalid date
  if (isNaN(date.getTime())) {
    return 'Older';
  }

  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (isSameDay(date, today)) {
    return 'Today';
  }

  if (isSameDay(date, yesterday)) {
    return 'Yesterday';
  }

  if (days < 7) {
    return 'This Week';
  }

  if (days < 30) {
    return 'This Month';
  }

  return 'Older';
}

// Helper functions
function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

function formatTime(date: Date): string {
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  const displayMinutes = minutes.toString().padStart(2, '0');
  return `${displayHours}:${displayMinutes} ${ampm}`;
}

function formatShortDate(date: Date): string {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}`;
}

function formatMonthDay(date: Date): string {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}`;
}

function formatFullDate(date: Date): string {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}
